import React, { useEffect, useRef, useState } from "react";
import { Typography } from "../../../components/typography/TypographyComponents";
import { FlagIcon } from "../../region/FlagIcon";
import { COUNTRY_CODES, CountryCode, REGIONS } from "../../region/region";

type Props = {
  country: CountryCode;
  isAdmin: boolean;
  /** Edit mode (Admins only): pick one of the four countries from a dropdown. */
  onChange?: (code: CountryCode) => void;
};

/** Settings > Company "Country" card (owner's mockup, 2026-10-24): no
 * explanation of what the country changes, on purpose. */
export const CountryCard: React.FC<Props> = ({ country, isAdmin, onChange }) => {
  const editing = !!onChange && isAdmin;
  const region = REGIONS[country];
  return (
    <div
      data-testid={"country-card"}
      className="flex items-start justify-between rounded-xl border border-[#E4E9F2] bg-white p-6 max-md:flex-col max-md:gap-2 max-md:p-4">
      <div className="w-[42%] max-md:w-full">
        <Typography fontcss="subtitle3" colorcss="bodyprimary">
          Country
        </Typography>
        <Typography fontcss="cardSub" colorcss="bodytertiary">
          Where your firm is located.
        </Typography>
        {!isAdmin && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#EDEFF3] bg-[#F7F8FA] px-3 py-1.5 text-[13px] text-[#5B6478]">
            <span aria-hidden>🔒</span>Only Admins can change the country.
          </div>
        )}
      </div>
      {editing ? (
        <div className="flex-1 py-3 max-md:w-full">
          <CountryDropdown country={country} onChange={(code) => onChange?.(code)} />
        </div>
      ) : (
        <div className="flex-1 py-3 max-md:w-full">
          <Typography fontcss="body2Regular" colorcss="bodytertiary">
            Country
          </Typography>
          <div className="mt-1 flex items-center gap-2.5 text-[16px] font-semibold text-[#1F2433]">
            <FlagIcon code={country} />
            {region.name}
          </div>
        </div>
      )}
    </div>
  );
};

/** Roadmap 1883 (owner 2026-10-25): a dropdown like Province / State, with flag
 * pictures (a plain <select> can't show them, and emoji flags don't show on Windows). */
const CountryDropdown: React.FC<{
  country: CountryCode;
  onChange: (code: CountryCode) => void;
}> = ({ country, onChange }) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button
        type={"button"}
        aria-label={"Country"}
        aria-haspopup={"listbox"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex h-10 w-full items-center gap-2.5 rounded-lg border bg-white px-3 text-left text-sm font-medium text-[#1D2C3B] outline-none transition-all duration-300 ${
          open
            ? "border-[#6938EF] shadow-settings-purple"
            : "border-[#C8D2DE]"
        }`}>
        <FlagIcon code={country} />
        <span className="flex-1">{REGIONS[country].name}</span>
        <svg
          aria-hidden
          width={12}
          height={12}
          viewBox={"0 0 12 12"}
          className={open ? "rotate-180" : ""}>
          <path
            d={"M2.5 4.5 6 8l3.5-3.5"}
            fill={"none"}
            stroke={"#5F758F"}
            strokeWidth={1.5}
            strokeLinecap={"round"}
            strokeLinejoin={"round"}
          />
        </svg>
      </button>
      {open && (
        <ul
          role={"listbox"}
          aria-label={"Country"}
          className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg border border-[#DCE2EA] bg-white py-1 shadow-[0_8px_24px_rgba(31,36,51,0.12)]">
          {COUNTRY_CODES.map((code) => {
            const selected = code === country;
            return (
              <li
                key={code}
                role={"option"}
                aria-selected={selected}
                onClick={() => {
                  onChange(code);
                  setOpen(false);
                }}
                className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#1D2C3B] ${
                  selected ? "bg-[#F4F1FF]" : "hover:bg-[#F7F8FA]"
                }`}>
                <FlagIcon code={code} />
                <span className="flex-1">{REGIONS[code].name}</span>
                {selected && (
                  <span aria-hidden className="text-[#6B4EFF]">
                    ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
